import React from "react";
import { ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import numbro from "numbro";
import startCase from "lodash/startCase";
import { currencyPosition } from "../../../utils/currency-position";
import { useLightTheme } from "../../../hooks/useLightTheme";
import "./areaChart.scss";

const TooltipItem = ({ name, value, style, children }) => {
  return (
    <div className="recharts-tooltip-item" style={style}>
      <span className="recharts-tooltip-item-name">{name}</span>
      <span className="recharts-tooltip-item-separator">{": "}</span>
      <span className="recharts-tooltip-item-value">{value}</span>
      {children}
    </div>
  );
};

const TooltipContainer = ({ label, children, style, labelStyle }) => (
  <div className="recharts-default-tooltip" style={style}>
    <p className="recharts-tooltip-label" style={{ ...labelStyle, margin: 0 }}>
      {label}
    </p>
    {children}
  </div>
);

const toPercent = (decimal) => {
  const percent = decimal * 100;
  return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(2)}%`;
};

const getPercent = (value, total) => {
  const ratio = total > 0 ? value / total : 0;

  return toPercent(ratio);
};

const AreaChart = (props) => {
  const isLight = useLightTheme();

  const {
    xTickStyle = { stroke: "none", fill: isLight ? "#717173" : "#5b606a" },
    yTickStyle = { stroke: "none", fill: isLight ? "#717173" : "#5b606a" },
    cartesianGridVerticalPoints,
    previousDataKey = "lastYear",
    currentDataKey = "thisYear",
    interval = "preserveEnd",
    strokeDasharray = "3 3",
    toolTipContent = false,
    hideLegend = false,
    hasRawData = false,
    currencySymbol = "",
    chartData = [],
    height = 300,
    tooltipName,
    roundPercentage,
    width,
    margin,
    percentageBaseKey,
    series = null
  } = props;

  const formatter = (value) => {
    let formattedNumber;

    if (value < 1000) {
      formattedNumber = numbro(value).format({
        thousandSeparated: false,
        mantissa: 0,
        trimMantissa: true
      });
    } else {
      formattedNumber = numbro(value).format({
        average: true,
        mantissa: 0,
        optionalMantissa: true
      });
    }

    if (currencySymbol === "%") {
      return `${formattedNumber}%`;
    }

    return currencyPosition(formattedNumber, currencySymbol);
  };

  const toolTipFormatter = (value, name) => {
    const formattedNumber = numbro(value).format({
      thousandSeparated: true,
      mantissa: props.decimal || 0,
      trimMantissa: false
    });

    const singleSeriesName = isPreviousDataEmpty() && tooltipName ? tooltipName : startCase(name);
    if (currencySymbol === "%") {
      return [`${roundPercentage ? Math.round(value) : value}%`, singleSeriesName];
    }

    return [currencyPosition(formattedNumber, currencySymbol), singleSeriesName];
  };

  const legendFormatter = (value, entry) => {
    return <span style={{ color: entry.color }}>{startCase(value)}</span>;
  };

  const handleClick = (e) => {
    if (hasRawData) {
      window.open(`${e.activePayload[0].payload.rawDataUrl}`, "_blank");
    }
  };

  const isPreviousDataEmpty = () => {
    if (!chartData.length) {
      return true;
    }
    const lastYear = chartData.map((val) => val[previousDataKey] || 0);
    return Math.max(...lastYear) === 0;
  };

  const renderCustomTooltip = () => {
    const CustomTooltip = ({ active, payload = [], label, contentStyle, labelStyle }) => {
      const finalItemStyle = {
        display: "block",
        paddingTop: 0,
        paddingBottom: 0,
        color: "#0099ce"
      };

      const finalStyleWrapper = {
        margin: 0,
        padding: 10,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        whiteSpace: "nowrap",
        ...contentStyle
      };

      if (!active || !payload?.length) return null;

      if (percentageBaseKey) {
        if (payload[0].payload?.error) {
          return (
            <TooltipContainer style={finalStyleWrapper} labelStyle={labelStyle} label={label}>
              <div>{payload[0].payload?.error}</div>
            </TooltipContainer>
          );
        }

        return (
          <TooltipContainer style={finalStyleWrapper} labelStyle={labelStyle} label={label}>
            {payload.map((entry, index) => {
              const [value, name] = toolTipFormatter(entry.value, entry.name);
              const showPercent = entry.dataKey !== percentageBaseKey;
              const baseValue = entry.payload?.[percentageBaseKey];
              const percent =
                showPercent && baseValue !== undefined
                  ? `(${getPercent(entry.value, baseValue)})`
                  : "";

              return (
                <TooltipItem
                  key={`item-${index}`}
                  name={name}
                  value={`${value} ${percent}`}
                  style={{ color: entry.color }}
                />
              );
            })}
          </TooltipContainer>
        );
      }

      let [value, name] = toolTipFormatter(payload[0].value, props.tooltipName);
      return (
        <TooltipContainer style={finalStyleWrapper} labelStyle={labelStyle} label={label}>
          <TooltipItem name={name} value={value} style={finalItemStyle}>
            <p style={{ color: "0099ce", margin: 0 }}>
              {payload[0].payload.additionalTooltip + currencySymbol}
            </p>
          </TooltipItem>
        </TooltipContainer>
      );
    };

    return CustomTooltip;
  };

  const verticalPoints =
    typeof cartesianGridVerticalPoints === "function"
      ? cartesianGridVerticalPoints(width)
      : undefined;

  const chartStyle = {
    width,
    height,
    margin
  };

  const renderAreas = () => {
    if (Array.isArray(series) && series.length) {
      return series.map((item) => (
        <Area
          key={item.dataKey}
          yAxisId={item.yAxisId || "left"}
          type="monotone"
          dataKey={item.dataKey}
          stroke={item.color}
          fill={item.fill || item.color}
          name={item.name}
        />
      ));
    }

    return (
      <>
        {!isPreviousDataEmpty() && (
          <Area
            yAxisId="left"
            type="monotone"
            dataKey={previousDataKey}
            stroke="#42b77a"
            fill="#42b77a"
          />
        )}
        <Area
          yAxisId="left"
          type="monotone"
          dataKey={currentDataKey}
          stroke="#0099ce"
          fill="#0099ce"
        />
      </>
    );
  };

  return (
    <ComposedChart data={chartData} onClick={handleClick} {...chartStyle}>
      <CartesianGrid
        verticalPoints={verticalPoints}
        strokeDasharray={strokeDasharray}
        stroke={isLight ? "#e6e7e8" : "#696969"}
        allowDecimals
      />
      <XAxis dataKey="name" tick={xTickStyle} allowDecimals interval={interval} />
      <YAxis yAxisId="left" tickFormatter={formatter} tick={yTickStyle} />
      <Tooltip
        formatter={toolTipFormatter}
        content={toolTipContent && renderCustomTooltip()}
        separator=": "
        itemStyle={{ paddingTop: 0, paddingBottom: 0 }}
        contentStyle={
          isLight
            ? { backgroundColor: "#fff", borderColor: "#e6e7e8" }
            : { backgroundColor: "#353942", borderColor: "#484b52" }
        }
        labelStyle={{ color: isLight ? "#1F1F20" : "#ccc", fontSize: "14px", fontWeight: 500 }}
      />
      {!hideLegend && <Legend formatter={legendFormatter} />}
      {renderAreas()}
    </ComposedChart>
  );
};

export default AreaChart;
